<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-chart-timeline-variant me-2"></i>{{ $t('page.maternal_mortality', 'Maternal Mortality & Near-Miss') }}</h4>
        </div>
        <div class="d-flex gap-2">
          <select v-model="months" class="hms-input" @change="load" style="max-width:160px">
            <option :value="3">Last 3 months</option>
            <option :value="6">Last 6 months</option>
            <option :value="12">Last 12 months</option>
            <option :value="24">Last 24 months</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="load"><i class="mdi mdi-refresh"></i></button>
        </div>
      </div>

      <!-- Headline indicators -->
      <div class="hms-grid-4">
        <div class="hms-kpi">
          <div class="hms-kpi-label">MMR (per 100k live births)</div>
          <div class="hms-kpi-num">{{ data.mmr !== null ? data.mmr : '—' }}</div>
          <div class="hms-kpi-sub">{{ data.maternal_deaths || 0 }} deaths / {{ data.live_births || 0 }} live births</div>
        </div>
        <div class="hms-kpi">
          <div class="hms-kpi-label">SAMM (near-miss / 1k LB)</div>
          <div class="hms-kpi-num">{{ data.samm_per_1000 !== null ? data.samm_per_1000 : '—' }}</div>
          <div class="hms-kpi-sub">{{ data.near_miss_count || 0 }} near-misses</div>
        </div>
        <div class="hms-kpi">
          <div class="hms-kpi-label">Mortality index</div>
          <div class="hms-kpi-num">{{ data.mortality_index !== null ? data.mortality_index + '%' : '—' }}</div>
          <div class="hms-kpi-sub">deaths / (deaths + near-misses)</div>
        </div>
        <div class="hms-kpi">
          <div class="hms-kpi-label">Preventable</div>
          <div class="hms-kpi-num">{{ data.preventable || 0 }}</div>
          <div class="hms-kpi-sub">{{ data.possibly_preventable || 0 }} possibly preventable</div>
        </div>
      </div>

      <!-- Charts row -->
      <div class="hms-grid-2 mt-3">
        <div class="hms-chart-card">
          <h6><i class="mdi mdi-chart-donut"></i> Causes of maternal death (ICD-MM)</h6>
          <div v-if="totalCauses === 0" class="hms-muted text-center hms-pad">No maternal deaths in this window.</div>
          <div v-else>
            <div v-for="(n, k) in data.causes" :key="k" class="hms-bar-row">
              <div class="hms-bar-label">{{ causeLabel(k) }}</div>
              <div class="hms-bar-track">
                <div class="hms-bar-fill" :class="'hms-bar-' + k" :style="{ width: ((n/totalCauses)*100) + '%' }"></div>
              </div>
              <div class="hms-bar-value">{{ n }} ({{ Math.round((n/totalCauses)*100) }}%)</div>
            </div>
          </div>
        </div>

        <div class="hms-chart-card">
          <h6><i class="mdi mdi-account-clock"></i> 3-Delays Model (deaths + near-misses)</h6>
          <div class="hms-delay-card">
            <div class="hms-delay-num">{{ data.delays?.first_delay || 0 }}</div>
            <div>
              <strong>Delay 1: decision to seek care</strong>
              <div class="hms-muted-sm">Recognition of danger signs, household decision-making</div>
            </div>
          </div>
          <div class="hms-delay-card">
            <div class="hms-delay-num">{{ data.delays?.second_delay || 0 }}</div>
            <div>
              <strong>Delay 2: reaching care</strong>
              <div class="hms-muted-sm">Transport, distance, road conditions, cost</div>
            </div>
          </div>
          <div class="hms-delay-card">
            <div class="hms-delay-num">{{ data.delays?.third_delay || 0 }}</div>
            <div>
              <strong>Delay 3: adequate care at facility</strong>
              <div class="hms-muted-sm">Staff, supplies, blood, theatre, skill mix</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend chart -->
      <div class="hms-chart-card mt-3">
        <h6><i class="mdi mdi-chart-line"></i> Monthly trend — deaths · near-misses · live births</h6>
        <svg viewBox="0 0 700 220" class="hms-trend-svg">
          <!-- grid -->
          <line v-for="i in 4" :key="'g'+i" :x1="40" :x2="680" :y1="40 + i*40" :y2="40 + i*40" stroke="#edf2f7" />
          <!-- axes -->
          <line x1="40" x2="40" y1="20" y2="200" stroke="#cbd5e0" />
          <line x1="40" x2="680" y1="200" y2="200" stroke="#cbd5e0" />
          <!-- y-axis labels -->
          <text v-for="i in 5" :key="'yl'+i" x="32" :y="44 + (i-1)*40" font-size="9" text-anchor="end" fill="#718096">{{ Math.round(yMax * (5-i) / 4) }}</text>
          <!-- live births bars -->
          <rect v-for="(t, i) in data.trend" :key="'lb'+i"
                :x="50 + i*54" :width="48"
                :y="200 - barH(t.live_births)" :height="barH(t.live_births)"
                fill="#bee3f8" rx="3" />
          <!-- deaths bars -->
          <rect v-for="(t, i) in data.trend" :key="'d'+i"
                :x="50 + i*54" :width="22"
                :y="200 - barH(t.deaths)" :height="barH(t.deaths)"
                fill="#dc2626" />
          <!-- near miss bars -->
          <rect v-for="(t, i) in data.trend" :key="'nm'+i"
                :x="74 + i*54" :width="22"
                :y="200 - barH(t.near_miss)" :height="barH(t.near_miss)"
                fill="#ea580c" />
          <!-- month labels -->
          <text v-for="(t, i) in data.trend" :key="'m'+i"
                :x="74 + i*54" y="215" font-size="9" text-anchor="middle" fill="#4a5568">{{ shortMonth(t.month) }}</text>
        </svg>
        <div class="d-flex justify-content-center gap-3 hms-muted-sm">
          <span><span class="hms-legend-swatch" style="background:#dc2626"></span> Deaths</span>
          <span><span class="hms-legend-swatch" style="background:#ea580c"></span> Near-miss</span>
          <span><span class="hms-legend-swatch" style="background:#bee3f8"></span> Live births</span>
        </div>
      </div>

      <!-- MPDSR compliance -->
      <div class="hms-chart-card mt-3">
        <h6><i class="mdi mdi-clipboard-check"></i> MPDSR compliance ({{ data.compliance?.total_reviews || 0 }} reviews)</h6>
        <div class="hms-compliance-row">
          <div class="hms-comp-cell">
            <div class="hms-comp-num">{{ pct(data.compliance?.notified_within_48h, data.compliance?.total_reviews) }}</div>
            <div class="hms-comp-lbl">Notified ≤48 h</div>
            <div class="hms-muted-sm">{{ data.compliance?.notified_within_48h || 0 }}/{{ data.compliance?.total_reviews || 0 }}</div>
          </div>
          <div class="hms-comp-cell">
            <div class="hms-comp-num">{{ pct(data.compliance?.reviewed, data.compliance?.total_reviews) }}</div>
            <div class="hms-comp-lbl">Reviewed (≤7 d)</div>
            <div class="hms-muted-sm">{{ data.compliance?.reviewed || 0 }}/{{ data.compliance?.total_reviews || 0 }}</div>
          </div>
          <div class="hms-comp-cell">
            <div class="hms-comp-num">{{ pct(data.compliance?.closed, data.compliance?.total_reviews) }}</div>
            <div class="hms-comp-lbl">Closed (action plan signed)</div>
            <div class="hms-muted-sm">{{ data.compliance?.closed || 0 }}/{{ data.compliance?.total_reviews || 0 }}</div>
          </div>
          <div class="hms-comp-cell">
            <div class="hms-comp-num">{{ pct(data.autopsies, data.maternal_deaths) }}</div>
            <div class="hms-comp-lbl">Autopsy rate</div>
            <div class="hms-muted-sm">{{ data.autopsies || 0 }}/{{ data.maternal_deaths || 0 }}</div>
          </div>
        </div>
      </div>

      <!-- Near-miss register -->
      <div class="d-flex justify-content-between align-items-center mt-4 mb-2">
        <h4 class="hms-h4 m-0"><i class="mdi mdi-account-alert"></i> Near-miss register</h4>
        <button class="hms-btn hms-btn-blue" @click="openNmModal()"><i class="mdi mdi-plus"></i> Record near-miss</button>
      </div>

      <table class="hms-table">
        <thead><tr><th>Date</th><th>Patient</th><th>Age</th><th>Phase</th><th>Cause</th><th>Interventions</th><th>Delays</th><th>Outcome</th><th>Preventable</th><th></th></tr></thead>
        <tbody>
          <tr v-for="n in nearMissList" :key="n.nm_id">
            <td>{{ formatDate(n.event_date) }}</td>
            <td>
              <strong>{{ n.patient_name }}</strong>
              <div class="hms-muted-sm">{{ n.patient_no }}</div>
            </td>
            <td>{{ n.age_at_event }}</td>
            <td>{{ (n.pregnancy_status || '').replace(/_/g,' ') }}</td>
            <td><span class="hms-pill" :class="causeBucketPill(n.primary_cause)">{{ n.primary_cause.replace(/_/g,' ') }}</span></td>
            <td class="hms-muted-sm" style="max-width:240px">{{ (n.intervention_markers || '').replace(/_/g,' ').replace(/,/g, ' · ') || '—' }}</td>
            <td>
              <span v-if="n.first_delay==1" class="hms-pill hms-pill-warn">1</span>
              <span v-if="n.second_delay==1" class="hms-pill hms-pill-warn">2</span>
              <span v-if="n.third_delay==1" class="hms-pill hms-pill-danger">3</span>
            </td>
            <td><span class="hms-pill" :class="outcomePill(n.final_outcome)">{{ n.final_outcome.replace(/_/g,' ') }}</span></td>
            <td><span class="hms-pill" :class="preventablePill(n.preventable)">{{ (n.preventable || '').replace(/_/g,' ') }}</span></td>
            <td><button class="hms-btn hms-btn-secondary hms-btn-sm" @click="editNm(n)"><i class="mdi mdi-pencil"></i></button></td>
          </tr>
          <tr v-if="!nearMissList.length"><td colspan="10" class="text-center hms-pad hms-muted">No near-miss cases recorded.</td></tr>
        </tbody>
      </table>

      <!-- Near-miss modal -->
      <div v-if="nmModal" class="hms-modal-bg" @click.self="nmModal=false">
        <div class="hms-modal">
          <div class="hms-modal-head">
            <h5><i class="mdi mdi-account-alert"></i> {{ nmForm.nm_id ? 'Edit' : 'Record' }} maternal near-miss</h5>
            <button class="hms-btn hms-btn-icon" @click="nmModal=false"><i class="mdi mdi-close"></i></button>
          </div>
          <div class="hms-modal-body">
            <div class="hms-form-grid">
              <label>Patient ID <input type="number" v-model="nmForm.patient_id" class="hms-input" :disabled="!!nmForm.nm_id" /></label>
              <label>Event date <input type="date" v-model="nmForm.event_date" class="hms-input" /></label>
              <label>Pregnancy phase
                <select v-model="nmForm.pregnancy_status" class="hms-input">
                  <option value="pregnant">Antenatal</option>
                  <option value="intrapartum">Intrapartum</option>
                  <option value="postpartum_24h">Postpartum &lt;24h</option>
                  <option value="postpartum_42d">Postpartum 1-42d</option>
                </select>
              </label>
              <label>Gestation (wks) <input type="number" v-model="nmForm.gestation_at_event" class="hms-input" /></label>
              <label>Primary cause
                <select v-model="nmForm.primary_cause" class="hms-input">
                  <option value="haemorrhage">Haemorrhage</option>
                  <option value="hypertensive">Hypertensive disorder</option>
                  <option value="sepsis">Sepsis / infection</option>
                  <option value="abortion_related">Abortion-related</option>
                  <option value="obstructed_labour">Obstructed labour / rupture</option>
                  <option value="ectopic">Ectopic pregnancy</option>
                  <option value="indirect_medical">Indirect (medical)</option>
                  <option value="anaesthesia_related">Anaesthesia</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>Cause detail <input v-model="nmForm.primary_cause_detail" class="hms-input" /></label>
            </div>

            <h6 class="mt-3"><i class="mdi mdi-clipboard-list"></i> WHO clinical criteria (any qualifies)</h6>
            <div class="hms-chip-row">
              <label v-for="opt in clinicalCriteriaOpts" :key="opt.code" class="hms-chip">
                <input type="checkbox" :value="opt.code" v-model="nmClinicalArr" /> {{ opt.label }}
              </label>
            </div>

            <h6 class="mt-3"><i class="mdi mdi-medical-bag"></i> Intervention markers</h6>
            <div class="hms-chip-row">
              <label v-for="opt in interventionOpts" :key="opt.code" class="hms-chip">
                <input type="checkbox" :value="opt.code" v-model="nmInterventionArr" /> {{ opt.label }}
              </label>
            </div>

            <h6 class="mt-3"><i class="mdi mdi-organ"></i> Organ systems affected</h6>
            <div class="hms-chip-row">
              <label v-for="opt in organOpts" :key="opt" class="hms-chip">
                <input type="checkbox" :value="opt" v-model="nmOrganArr" /> {{ opt }}
              </label>
            </div>

            <div class="hms-form-grid mt-3">
              <label>Units blood transfused <input type="number" v-model="nmForm.units_blood_transfused" class="hms-input" /></label>
              <label>ICU days <input type="number" v-model="nmForm.icu_days" class="hms-input" /></label>
              <label>Hospital stay (d) <input type="number" v-model="nmForm.hospital_stay_days" class="hms-input" /></label>
              <label>Final outcome
                <select v-model="nmForm.final_outcome" class="hms-input">
                  <option value="full_recovery">Full recovery</option>
                  <option value="sequelae">Recovery with sequelae</option>
                  <option value="transferred">Transferred out</option>
                  <option value="died_later">Died later (would convert to mortality)</option>
                </select>
              </label>
            </div>

            <h6 class="mt-3"><i class="mdi mdi-account-clock"></i> 3-Delays analysis</h6>
            <div class="d-flex gap-3">
              <label class="hms-check"><input type="checkbox" v-model="nmForm.first_delay" :true-value="1" :false-value="0" /> Delay 1 (decision)</label>
              <label class="hms-check"><input type="checkbox" v-model="nmForm.second_delay" :true-value="1" :false-value="0" /> Delay 2 (reach)</label>
              <label class="hms-check"><input type="checkbox" v-model="nmForm.third_delay" :true-value="1" :false-value="0" /> Delay 3 (facility)</label>
            </div>
            <label class="d-block mt-2">Delay notes <textarea v-model="nmForm.delay_notes" rows="2" class="hms-input"></textarea></label>

            <div class="hms-form-grid mt-3">
              <label>Preventability
                <select v-model="nmForm.preventable" class="hms-input">
                  <option value="preventable">Preventable</option>
                  <option value="possibly_preventable">Possibly preventable</option>
                  <option value="non_preventable">Non-preventable</option>
                  <option value="undetermined">Undetermined</option>
                </select>
              </label>
            </div>
            <label class="d-block mt-2">Action plan <textarea v-model="nmForm.action_plan" rows="2" class="hms-input"></textarea></label>
          </div>
          <div class="hms-modal-foot">
            <button class="hms-btn hms-btn-secondary" @click="nmModal=false">Cancel</button>
            <button class="hms-btn hms-btn-success" :disabled="!nmForm.patient_id || saving" @click="saveNm">{{ saving ? 'Saving…' : 'Save near-miss' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MaternalMortality",
  components: { Navigation },
  data() {
    return {
      months: 12, saving: false,
      data: { causes: {}, places: [], delays: {}, trend: [], compliance: {} },
      nearMissList: [],
      nmModal: false, nmForm: this.blankNmForm(),
      nmClinicalArr: [], nmInterventionArr: [], nmOrganArr: []
    };
  },
  computed: {
    totalCauses() { return Object.values(this.data.causes || {}).reduce((a,b) => a + Number(b||0), 0); },
    yMax() {
      const t = this.data.trend || [];
      let m = 0;
      t.forEach(r => { m = Math.max(m, Number(r.live_births||0), Number(r.deaths||0)*5, Number(r.near_miss||0)*3); });
      return Math.max(m, 10);
    },
    clinicalCriteriaOpts() { return [
      { code:'eclampsia', label:'Eclampsia' }, { code:'severe_pre_eclampsia', label:'Severe pre-eclampsia' },
      { code:'severe_PPH', label:'Severe PPH' }, { code:'sepsis', label:'Sepsis' },
      { code:'rupture_uterus', label:'Uterine rupture' }, { code:'severe_complications_abortion', label:'Severe abortion complications' },
      { code:'ectopic_pregnancy', label:'Ectopic' }, { code:'hellp_syndrome', label:'HELLP' },
      { code:'amniotic_fluid_embolism', label:'AFE' }, { code:'pulmonary_embolism', label:'PE' },
      { code:'status_epilepticus', label:'Status epilepticus' }, { code:'cardiac_arrest', label:'Cardiac arrest' },
      { code:'shock_septic', label:'Septic shock' }, { code:'shock_hypovolaemic', label:'Hypovolaemic shock' },
      { code:'severe_acute_asthma', label:'Severe asthma' }, { code:'cyanosis', label:'Cyanosis' },
      { code:'gasping', label:'Gasping' }, { code:'respiratory_rate_extreme', label:'RR extreme' },
      { code:'oliguria_unresponsive', label:'Oliguria' }, { code:'clotting_failure', label:'DIC' },
      { code:'loss_consciousness_12h', label:'LOC >12h' }, { code:'stroke', label:'Stroke' }
    ]; },
    interventionOpts() { return [
      { code:'cardiopulmonary_resuscitation', label:'CPR' },
      { code:'massive_blood_transfusion', label:'Massive transfusion (≥5u)' },
      { code:'hysterectomy', label:'Hysterectomy' },
      { code:'dialysis_acute', label:'Acute dialysis' },
      { code:'intubation_ventilation', label:'Intubation/ventilation' },
      { code:'icu_admission', label:'ICU admission' },
      { code:'laparotomy_emergency', label:'Emergency laparotomy' },
      { code:'interventional_radiology', label:'IR procedure' }
    ]; },
    organOpts() { return ['cardiovascular','respiratory','renal','hepatic','neurological','haematological','uterine']; }
  },
  mounted() { this.load(); },
  methods: {
    blankNmForm() { return { nm_id:null, patient_id:null, event_date:new Date().toISOString().substring(0,10),
      pregnancy_status:'postpartum_24h', gestation_at_event:null,
      primary_cause:'haemorrhage', primary_cause_detail:'',
      units_blood_transfused:0, icu_days:null, hospital_stay_days:null,
      first_delay:0, second_delay:0, third_delay:0, delay_notes:'',
      final_outcome:'full_recovery', preventable:'undetermined', action_plan:'' }; },
    async load() {
      try {
        const r = await executeJson({ function:'getMortalityDashboard', months:this.months }, hms);
        if (r.data && r.data.success) this.data = r.data.data || {};
        const n = await executeJson({ function:'getNearMissList' }, hms);
        if (n.data && n.data.success) this.nearMissList = n.data.data.rows || [];
      } catch (e) { console.error(e); }
    },
    openNmModal() {
      this.nmForm = this.blankNmForm();
      this.nmClinicalArr = []; this.nmInterventionArr = []; this.nmOrganArr = [];
      this.nmModal = true;
    },
    editNm(n) {
      this.nmForm = { ...n };
      this.nmClinicalArr = (n.clinical_criteria || '').split(',').filter(x => x);
      this.nmInterventionArr = (n.intervention_markers || '').split(',').filter(x => x);
      this.nmOrganArr = (n.organ_systems_affected || '').split(',').filter(x => x);
      this.nmModal = true;
    },
    async saveNm() {
      this.saving = true;
      try {
        const payload = { function:'recordNearMiss', ...this.nmForm,
          clinical_criteria: this.nmClinicalArr.join(','),
          intervention_markers: this.nmInterventionArr.join(','),
          organ_systems_affected: this.nmOrganArr.join(',') };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) { this.nmModal = false; await this.load(); }
        else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    barH(v) { const m = this.yMax || 1; return Math.min(160, (Number(v||0) / m) * 160); },
    shortMonth(m) { if (!m) return ''; const d = new Date(m + '-01T00:00:00'); return d.toLocaleString('en', { month:'short' }); },
    causeLabel(k) { return ({ haemorrhage:'Haemorrhage', hypertensive:'Hypertensive', sepsis:'Sepsis', abortion:'Abortion-related', obstructed_labour:'Obstructed labour', embolism:'Embolism', indirect:'Indirect (medical)', other:'Other' })[k] || k; },
    causeBucketPill(c) { return ({ haemorrhage:'hms-pill-danger', hypertensive:'hms-pill-warn', sepsis:'hms-pill-warn', abortion_related:'hms-pill-warn', obstructed_labour:'hms-pill-warn', ectopic:'hms-pill-warn', indirect_medical:'hms-pill-info', anaesthesia_related:'hms-pill-info', other:'hms-pill-muted' })[c] || 'hms-pill-muted'; },
    outcomePill(o) { return ({ full_recovery:'hms-pill-success', sequelae:'hms-pill-warn', transferred:'hms-pill-info', died_later:'hms-pill-danger' })[o] || 'hms-pill-muted'; },
    preventablePill(p) { return ({ preventable:'hms-pill-danger', possibly_preventable:'hms-pill-warn', non_preventable:'hms-pill-info', undetermined:'hms-pill-muted' })[p] || 'hms-pill-muted'; },
    pct(n, d) { const num = Number(n) || 0; const den = Number(d) || 0; if (den === 0) return '—'; return Math.round((num/den)*100) + '%'; },
    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ','T')); return isNaN(dt) ? d : dt.toLocaleDateString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-h4 { font-size: 14px; font-weight: 600; color: #2d3748; margin: 10px 0; }
.hms-page-title { font-size: 18px; font-weight: 600; }

.hms-grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.hms-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 900px) { .hms-grid-4 { grid-template-columns: 1fr 1fr; } .hms-grid-2 { grid-template-columns: 1fr; } }

.hms-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.hms-kpi-label { font-size: 11px; color: #718096; font-weight: 600; text-transform: uppercase; }
.hms-kpi-num { font-size: 28px; font-weight: 700; color: #2d3748; margin: 4px 0; }
.hms-kpi-sub { font-size: 11px; color: #718096; }

.hms-chart-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; }
.hms-chart-card h6 { font-size: 13px; font-weight: 600; color: #2d3748; margin-bottom: 12px; }

.hms-bar-row { display: grid; grid-template-columns: 140px 1fr 80px; gap: 10px; align-items: center; margin-bottom: 6px; font-size: 12px; }
.hms-bar-label { color: #4a5568; }
.hms-bar-track { background: #f7fafc; border-radius: 4px; height: 16px; overflow: hidden; }
.hms-bar-fill { height: 100%; }
.hms-bar-haemorrhage { background: #dc2626; }
.hms-bar-hypertensive { background: #ea580c; }
.hms-bar-sepsis { background: #d97706; }
.hms-bar-abortion { background: #9333ea; }
.hms-bar-obstructed_labour { background: #2563eb; }
.hms-bar-embolism { background: #0891b2; }
.hms-bar-indirect { background: #059669; }
.hms-bar-other { background: #94a3b8; }
.hms-bar-value { font-size: 11px; color: #4a5568; text-align: right; }

.hms-delay-card { display: flex; gap: 12px; align-items: center; background: #f7fafc; padding: 10px 12px; border-radius: 6px; margin-bottom: 8px; }
.hms-delay-num { font-size: 22px; font-weight: 700; color: #b91c1c; min-width: 36px; text-align: center; }

.hms-trend-svg { width: 100%; height: 220px; }
.hms-legend-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 4px; vertical-align: middle; }

.hms-compliance-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.hms-comp-cell { text-align: center; padding: 10px; background: #f7fafc; border-radius: 6px; }
.hms-comp-num { font-size: 24px; font-weight: 700; color: #22543d; }
.hms-comp-lbl { font-size: 11px; color: #4a5568; font-weight: 600; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
.hms-table th { background: #f7fafc; padding: 8px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 6px 8px; border-bottom: 1px solid #edf2f7; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; margin-right: 3px; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-muted, .hms-muted-sm { color: #a0aec0; }
.hms-muted-sm { font-size: 11px; }
.hms-pad { padding: 16px; }

.hms-input { width: 100%; padding: 7px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.hms-form-grid label { display: block; font-size: 12px; color: #4a5568; font-weight: 600; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }
.hms-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-chip { display: inline-flex; align-items: center; gap: 4px; background: #f7fafc; border: 1px solid #e2e8f0; padding: 4px 10px; border-radius: 14px; font-size: 12px; cursor: pointer; }
.hms-chip:has(input:checked) { background: #fed7d7; border-color: #fc8181; color: #742a2a; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue    { background: #2b6cb0; color: #fff; }
.hms-btn-success { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #edf2f7; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-icon { background: transparent; color: #4a5568; padding: 4px; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; width: 760px; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
</style>
